import { falSpinnerThird } from 'shared/@generated/icons/falSpinnerThird';
import { Text } from 'shared/components/text';
import { FaIcon, Icon } from 'shared/components/icon';
import { TouchableOpacity, View } from 'react-native';
import { Props } from 'shared/@types';
import { Link } from 'shared/components/link';
import { clsx } from 'clsx';

type ButtonBaseProps = Props<{
  textClassName?: string;
  text?: string;
  dense?: boolean;
  disabled?: boolean;
  loading?: boolean;
  center?: boolean;
  icon?: FaIcon;
  iconPosition?: 'left' | 'right';
  color?: 'primary' | 'secondary' | 'brown' | 'blue' | 'white' | 'boost';
  accessibilityLabel?: string;
  testID?: string;
}>;

export type ButtonProps =
  | (ButtonBaseProps & { href: string; openInNewTab?: boolean; onPress?: () => void })
  | (ButtonBaseProps & { href?: never; openInNewTab?: never; onPress: () => void });

const Button = ({
  text,
  dense,
  disabled,
  loading,
  center,
  icon,
  iconPosition = 'right',
  color = 'primary',
  accessibilityLabel,
  className,
  textClassName,
  testID,
  onPress,
  href,
  openInNewTab,
  children,
}: ButtonProps) => {
  let textWeight = 'font-black';
  let textColor = 'text-white';
  let bgStyle = 'bg-brand-green border-brand-green';
  let hoverStyle = 'hover:opacity-75';

  if (color === 'secondary') {
    textWeight = 'font-semibold underline decoration-brand-brown-dark/30';
    textColor = 'text-brand-brown-dark';
    bgStyle = 'bg-transparent border-transparent !p-0';
  }

  if (color === 'brown') {
    textWeight = 'font-bold';
    textColor = 'text-brand-cream';
    bgStyle = 'bg-brand-brown-dark border-brand-brown-dark';
    hoverStyle = 'hover:opacity-75';
  }

  if (color === 'blue') {
    textWeight = 'font-bold';
    bgStyle = 'border-blue-600 bg-blue-600';
  }

  if (color === 'white') {
    textWeight = 'font-semibold';
    textColor = 'text-brand-brown-dark';
    bgStyle = 'bg-white border-brand-brown-dark/15';
    hoverStyle = 'hover:border-brand-brown-dark/30';
  }

  if (color === 'boost') {
    textWeight = 'font-semibold';
    textColor = 'text-yellow-950';
    bgStyle = 'bg-yellow-400 border-yellow-500';
    hoverStyle = 'hover:border-brand-brown-dark/30';
  }

  if (loading) {
    disabled = true;
  }

  const containerClassName = `relative border flex !flex-row items-center rounded disabled:cursor-not-allowed disabled:opacity-75 ${bgStyle} ${className} ${center ? 'justify-center' : ''} ${!center && iconPosition === 'right' ? 'justify-between' : ''} ${disabled ? 'cursor-not-allowed' : hoverStyle} ${dense ? 'px-3 py-2' : 'px-4 py-3'}`;

  const content = (
    <>
      {icon && iconPosition === 'left' && (
        <Icon
          icon={icon}
          className={clsx(
            textClassName,
            textColor,
            dense ? 'my-0.5 size-4 md:my-1' : 'my-0.5 size-5 md:my-1',
            {
              'mr-3': text && !dense && color !== 'secondary',
              'mr-1': text && !dense && color == 'secondary',
              'mr-1.5': text && dense,
              'my-1': !text,
              'text-opacity-50': disabled,
            },
          )}
        />
      )}

      {text && (
        <Text
          className={clsx(
            textWeight,
            textClassName,
            textColor,
            dense ? 'text-sm md:text-base' : 'md:text-lg',
            {
              'text-opacity-50': disabled,
            },
          )}
        >
          {text}
        </Text>
      )}

      {children}

      {icon && iconPosition === 'right' && (
        <Icon
          icon={icon}
          className={clsx(
            textClassName,
            textColor,
            dense ? 'my-0.5 size-4 md:my-1' : 'my-0.5 size-5 md:my-1',
            {
              'ml-3': text && !dense && color !== 'secondary',
              'ml-1': text && !dense && color === 'secondary',
              'ml-1.5': text && dense,
              'my-1': !text,
              'text-opacity-50': disabled,
            },
          )}
        />
      )}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        openInNewTab={openInNewTab}
        containerClassName={containerClassName}
        asBlock={true}
        testID={testID}
        accessibilityLabel={accessibilityLabel}
        onPress={onPress}
      >
        {content}
      </Link>
    );
  }

  return (
    <TouchableOpacity
      className={containerClassName}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      onPress={onPress}
      disabled={disabled}
      testID={testID}
    >
      {loading && (
        <View
          className={`absolute inset-0 z-10 items-center justify-center rounded border ${bgStyle}`}
        >
          <Icon icon={falSpinnerThird} className={`size-8 animate-spin ${textColor}`} />
        </View>
      )}

      {content}
    </TouchableOpacity>
  );
};

export { Button };
